import { nextTick } from 'vue'
import type { ObjectDirective } from 'vue'

type InterpolationElement = HTMLElement & {
  $componentUpdated?: () => void
  $destroy?: () => void
}

export const vInterpolate: ObjectDirective<InterpolationElement> = {
  mounted(el) {
    updateLinks(el)

    el.$componentUpdated = () => {
      nextTick(() => updateLinks(el))
    }

    el.$destroy = () => {
      const links = getInternalLinks(el)
      removeListeners(links)
    }
  },
  updated: el => el.$componentUpdated?.(),
  beforeUnmount: el => el.$destroy?.(),
}

function updateLinks(el: InterpolationElement) {
  const links = getInternalLinks(el)
  addLocaleToLinks(links)
  removeTrailingSlashes(links)
  removeListeners(links)
  addListeners(links)
}

function getInternalLinks(el: HTMLElement): HTMLAnchorElement[] {
  return Array.from(el.getElementsByTagName('a')).filter((linkEl) => {
    const href = linkEl.getAttribute('href')
    return href && isInternalLink(href)
  })
}

function navigate(event: Event) {
  let target = event.target as HTMLElement | null
  // Traverse up the DOM tree to find the nearest anchor element
  while (target && target.tagName !== 'A') {
    target = target.parentElement
  }
  if (target && target.tagName === 'A') {
    const href = target.getAttribute('href')
    if (href) {
      event.preventDefault()

      // Check if it's the same site and has a hash
      const currentPath = window.location.pathname
      const [path, hash] = href.split('#')

      if (path === currentPath && hash) {
        // If it's the same page and has a hash, scroll to the element
        const element = document.getElementById(hash)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
        // Update the URL without reloading the page
        window.history.pushState(null, '', href)
      }
      else {
        // Otherwise, use Nuxt's navigateTo
        navigateTo(href)
      }
    }
  }
}

function addListeners(links: HTMLAnchorElement[]) {
  links.forEach((link) => {
    link.addEventListener('click', navigate, false)
  })
}

function removeListeners(links: HTMLAnchorElement[]) {
  links.forEach((link) => {
    link.removeEventListener('click', navigate, false)
  })
}

function isInternalLink(href?: string) {
  return href?.startsWith('/')
}

function addLocaleToLinks(links: HTMLAnchorElement[]) {
  links.forEach((link) => {
    const href = link.getAttribute('href')
    if (href && !href.startsWith('/en/')) {
      link.setAttribute('href', `/en${href}`)
    }
  })
}

function removeTrailingSlashes(links: HTMLAnchorElement[]) {
  links.forEach((link) => {
    const href = link.getAttribute('href')
    if (href) {
      link.setAttribute('href', href.replace(/\/$/, ''))
    }
  })
}
